import React, { useCallback, useRef, useState } from "react";

// Allowed image width breakpoints
const SIZE_BREAKPOINTS = [
  // Tailwind sizes
  24, 27, 30, 32, 36, 38, 48, 51, 56, 60, 72, 84, 96, 120, 126, 144, 168, 192,
  200, 240, 320, 336, 370, 375, 414, 540, 544, 600,
  // Beyond tailwind
  768, 900, 1024, 1080, 1200, 1366, 1440, 1600, 1728, 1944, 2160, 2376, 2592,
  2808, 3024,
];

// Package version, substituted from package.json at build time (see
// scripts/exportSource.js). Stamped onto every rendered element as
// data-sio-v so we can tell from the DOM which release is live.
const VERSION = "1.0.24";

// Extract all url(...) values from a CSS string
const extractUrlsFromString = (input) => {
  if (typeof input !== "string") return [];
  return [...input.matchAll(/url\((['"]?)(.*?)\1\)/g)].map((m) => m[2]);
};

// Replace all url(...) strings in a string using a getUrl resolver
const replaceUrlsInString = (input, getUrl) => {
  if (typeof input !== "string") return input;
  return input.replace(/url\((['"]?)(.*?)\1\)/g, (_, __, raw) => {
    return `url("${getUrl(raw)}")`;
  });
};

// Replace all url(...)s in inline styles using getUrl
const replaceStyleUrls = (style, getUrl) => {
  if (!style || typeof style !== "object") return style;
  const out = {};
  for (const key in style) {
    const value = style[key];
    out[key] =
      typeof value === "string" ? replaceUrlsInString(value, getUrl) : value;
  }
  return out;
};

// Replace all url(...)s in className using getUrl
const replaceClassNameUrls = (className, getUrl) =>
  typeof className === "string"
    ? replaceUrlsInString(className, getUrl)
    : className;

// Determine if a URL is from Unsplash
const isUnsplashUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "images.unsplash.com";
  } catch {
    return false;
  }
};

// Determine if a URL is from Shopify CDN
const isShopifyUrl = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.hostname === "cdn.shopify.com";
  } catch {
    return false;
  }
};

// Build an optimized Unsplash URL with specified width
const adjustUnsplashUrl = (src, width) => {
  try {
    const url = new URL(src);
    url.searchParams.set("w", width.toString());
    url.searchParams.set("q", "auto");
    url.searchParams.set("fit", "max");
    return url.toString();
  } catch {
    return src;
  }
};

// Build an optimized Shopify CDN URL with specified width
const adjustShopifyUrl = (src, width) => {
  try {
    const url = new URL(src);
    // Remove forced size suffix like _100x100 or _100x before extension
    url.pathname = url.pathname.replace(/_\d+x\d*(?=\.\w{3,4}$)/, "");
    url.searchParams.set("width", width.toString());
    return url.toString();
  } catch {
    return src;
  }
};

// Construct a Cloudinary fetch URL given a src and desired width
const buildCloudinaryUrl = (src, width) => {
  const isSvg = src.endsWith(".svg");
  const format = isSvg ? "svg" : "auto";
  return (
    "https://res.cloudinary.com/makerinc/image/fetch/" +
    "c_limit,w_" +
    width +
    ",f_" +
    format +
    ",dpr_2,q_auto/" +
    encodeURIComponent(src)
  );
};

// Is it already a cloudinary fetch URL
const isCloudinaryUrl = (url) => {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname === "res.cloudinary.com" &&
      parsed.pathname.startsWith("/makerinc/image/fetch/")
    );
  } catch {
    return false;
  }
};

// Update width in a pre-existing Cloudinary fetch URL
const adjustCloudinaryUrl = (src, width) => {
  try {
    const parsed = new URL(src);
    const pathParts = parsed.pathname.split("/");
    const fetchIndex = pathParts.indexOf("fetch");
    if (fetchIndex === -1 || fetchIndex + 2 > pathParts.length) return src;
    const transform = pathParts[fetchIndex + 1];
    let updated;
    if (/w_\d+/.test(transform)) {
      updated = transform.replace(/w_\d+/, `w_${width}`);
    } else {
      updated = `w_${width},${transform}`;
    }
    pathParts[fetchIndex + 1] = updated;
    parsed.pathname = pathParts.join("/");
    return parsed.toString();
  } catch {
    return src;
  }
};

// Synchronously measure a node, falling back to the viewport when the node
// can't be measured — no node yet (initial render, before the ref attaches)
// or no layout box. Returns { width, height }, or null only during SSR.
const measure = (node) => {
  const rect = node && node.getBoundingClientRect();
  if (rect && (rect.width > 0 || rect.height > 0)) {
    return { width: rect.width, height: rect.height };
  }
  return typeof window === "undefined"
    ? null
    : { width: window.innerWidth, height: window.innerHeight };
};

// Track an element's rendered size without relying on useLayoutEffect, which
// iOS Safari intermittently skips (bfcache restores, backgrounded mounts) and
// would leave the element stuck with no measurement.
//
// Instead we use a callback ref: React invokes it during the commit phase the
// moment the node attaches (and again with null on detach). Callback refs are
// not subject to the layout-effect-skip quirk, so the synchronous measurement
// always runs once the node exists. A ResizeObserver then keeps it in sync.
//
//   1. callback ref + getBoundingClientRect — synchronous read at attach time.
//   2. ResizeObserver — live updates on subsequent layout changes.
//   3. viewport — seeded initial state and last-resort fallback (see measure).
//
// Returns [callbackRef, size]; size is seeded from the viewport so a usable
// value exists on the very first render, before the node attaches.
const useMeasuredSize = () => {
  const [size, setSize] = useState(() => measure(null));
  const disconnectRef = useRef(null);

  const ref = useCallback((node) => {
    // Tear down the observer from a previous node (also runs on detach).
    if (disconnectRef.current) {
      disconnectRef.current();
      disconnectRef.current = null;
    }
    if (!node) return;

    // Synchronous read at attach time — the reliable replacement for the
    // layout-effect read that Safari could skip.
    setSize(measure(node));

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        if (width > 0 || height > 0) setSize({ width, height });
      });
      observer.observe(node);
      disconnectRef.current = () => observer.disconnect();
    }
  }, []);

  return [ref, size];
};

const BuildImageOptimizer = React.forwardRef(function BuildImageOptimizer(
  { originalTagName, children, ...props },
  forwardedRef,
) {
  const errorRef = useRef(false);
  const [hasLoadError, setHasLoadError] = useState(false);

  const { style, className, src, poster, type, alt, onError, ...rest } = props;

  // Reliable measurement via a callback ref instead of useLayoutEffect.
  const [ref, size] = useMeasuredSize();

  // Merge our internal measurement ref with the caller's forwarded ref so both
  // point at the same rendered node. Without this the babel plugin rewrites
  // e.g. `<video ref={x} poster=…>` into `<BuildImageOptimizer ref={x} …>` and the
  // ref is silently dropped (function components can't hold refs) — breaking
  // hls.js/lightbox libs that attach by ref. useCallback keeps the ref identity
  // stable across our own resize re-renders so it only fires on real
  // mount/unmount, not every measurement (a changing callback ref detaches and
  // re-attaches, tearing down an hls.js stream repeatedly).
  const setRefs = useCallback(
    (node) => {
      ref(node);
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [ref, forwardedRef],
  );

  // Reset the error latch whenever the source changes — done during render
  // (the supported "adjust state on prop change" pattern) rather than in an
  // effect, so it can't be skipped along with the layout effects.
  const prevSrcRef = useRef(src);
  if (prevSrcRef.current !== src) {
    prevSrcRef.current = src;
    errorRef.current = false;
    if (hasLoadError) setHasLoadError(false);
  }

  // Display width × device pixel ratio, derived during render straight from
  // the measured size — no effect on the critical path, so a skipped layout
  // effect can never leave this stale. After a load error we freeze the last
  // good width so a resize can't keep re-requesting a source we know fails;
  // the freeze clears when src changes (errorRef reset above).
  const frozenWidthRef = useRef(null);
  let exactWidth;
  if (errorRef.current) {
    exactWidth = frozenWidthRef.current;
  } else {
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    exactWidth = size ? size.width * dpr : null;
    frozenWidthRef.current = exactWidth;
  }

  // Round up to nearest matching Cloudinary width breakpoint. When there's no
  // measurement yet (SSR, where exactWidth is null), request the largest
  // breakpoint so we never ship a too-small image that has to be re-fetched.
  const width =
    exactWidth == null
      ? SIZE_BREAKPOINTS.at(-1)
      : SIZE_BREAKPOINTS.find((s) => s >= exactWidth) ?? SIZE_BREAKPOINTS.at(-1);

  // Get url based on current width
  const getUrl = (src) => {
    // Bypass data URIs - leave them untouched
    if (src && src.startsWith("data:")) {
      return src;
    }
    if (isUnsplashUrl(src)) {
      return adjustUnsplashUrl(src, width);
    }
    if (isShopifyUrl(src)) {
      return adjustShopifyUrl(src, width);
    }
    if (isCloudinaryUrl(src)) {
      return adjustCloudinaryUrl(src, width);
    }
    return buildCloudinaryUrl(src, width);
  };

  // Prepare props for rendering
  const Tag = originalTagName;

  const updatedStyle = replaceStyleUrls(style, getUrl);
  const updatedClassName = replaceClassNameUrls(className, getUrl);

  const handleError = (event) => {
    if (!errorRef.current) {
      errorRef.current = true;
      setHasLoadError(true);
    }
    if (typeof onError === "function") {
      onError(event);
    }
  };

  // Handle <img>
  if (originalTagName === "img") {
    return (
      <img
        {...rest}
        ref={setRefs}
        data-sio-v={VERSION}
        src={getUrl(src)}
        style={updatedStyle}
        className={updatedClassName}
        alt={alt ?? ""}
        onError={hasLoadError ? null : handleError}
      />
    );
  }

  // Handle <input type="image">
  if (originalTagName === "input" && type === "image") {
    return (
      <input
        {...rest}
        ref={setRefs}
        data-sio-v={VERSION}
        type="image"
        src={getUrl(src)}
        style={updatedStyle}
        className={updatedClassName}
        onError={hasLoadError ? null : handleError}
      />
    );
  }

  // Handle <video poster=...>
  if (originalTagName === "video") {
    return (
      <video
        {...rest}
        ref={setRefs}
        data-sio-v={VERSION}
        poster={getUrl(poster)}
        style={updatedStyle}
        className={updatedClassName}
        onError={onError}
      >
        {children}
      </video>
    );
  }

  // Default case for arbitrary components
  return (
    <Tag
      {...rest}
      ref={setRefs}
      data-sio-v={VERSION}
      style={updatedStyle}
      className={updatedClassName}
      onError={onError}
    >
      {children}
    </Tag>
  );
});

export default BuildImageOptimizer;
